import React, { useRef, useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faDownload, 
  faTrashAlt, 
  faList,
  faSyncAlt,
  faUpload,
  faCopy,
  faCheck
} from '@fortawesome/free-solid-svg-icons';

// Interface for firmware files
interface FirmwareFile {
  name: string;
  path: string;
  uploadedAt?: string;
}

interface BinariesManagerProps {
  firmwareFiles: FirmwareFile[];
  loadingFiles: boolean;
  onRefresh: () => void;
  onDelete: (fileName: string) => void;
  onViewAll: () => void;
  apiBaseUrl: string;
  limitDisplay?: number;
}

const BinariesManager: React.FC<BinariesManagerProps> = ({
  firmwareFiles,
  loadingFiles,
  onRefresh,
  onDelete,
  onViewAll,
  apiBaseUrl,
  limitDisplay = 6
}) => {
  // Upload state
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadSuccess, setUploadSuccess] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadFileName, setUploadFileName] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Copy state
  const [copiedPath, setCopiedPath] = useState<string | null>(null);

  // Get limited firmware files for the main view
  const limitedFirmwareFiles = firmwareFiles.slice(0, limitDisplay);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setUploadFileName(e.target.files[0].name);
    } else {
      setUploadFileName('');
    }
    // Clear previous messages
    setUploadSuccess(false);
    setUploadError(null);
  };

  const handleFileUpload = async () => {
    if (!fileInputRef.current?.files?.length) {
      setUploadError('Please select a file to upload');
      return;
    }

    const file = fileInputRef.current.files[0];
    
    // Validate file type and size
    if (!file.name.endsWith('.bin')) {
      setUploadError('Only .bin files are allowed');
      return;
    }

    // Optional: Add file size check (e.g., max 50MB)
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      setUploadError(`File is too large. Maximum file size is ${maxSize / 1024 / 1024}MB`);
      return;
    }

    setUploading(true);
    setUploadError(null);
    setUploadSuccess(false);

    const formData = new FormData();
    formData.append('firmware', file);

    try {
      const response = await axios.post(`${apiBaseUrl}/firmware/upload`, formData, {
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        // Increase timeout and body size limits
        timeout: 60000, // 60 seconds
        maxContentLength: Infinity,
        maxBodyLength: Infinity
      });
      
      // Successful upload
      setUploadSuccess(true);
      setUploadFileName('');
      
      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      // Trigger firmware files refresh
      onRefresh();
      
      // Auto-dismiss success message after 3 seconds
      setTimeout(() => {
        setUploadSuccess(false);
      }, 3000);
    } catch (error: any) {
      console.error('Error uploading file:', error);
      
      // Handle different types of errors
      if (error.response) {
        // The request was made and the server responded with a status code
        switch (error.response.status) {
          case 413:
            setUploadError('File is too large. Maximum file size is 50MB');
            break;
          case 400:
            setUploadError(error.response.data.message || 'Invalid file upload');
            break;
          case 403:
            setUploadError('You do not have permission to upload files');
            break;
          default:
            setUploadError(error.response.data.message || 'Failed to upload firmware file');
        }
      } else if (error.request) {
        // The request was made but no response was received
        setUploadError('No response from server. Please check your connection');
      } else {
        // Something happened in setting up the request
        setUploadError('Error preparing file upload');
      }
    } finally {
      setUploading(false);
    }
  };

  const handleCopyPath = (path: string) => {
    const fullPath = `${apiBaseUrl}${path}`;
    navigator.clipboard.writeText(fullPath).then(() => {
      setCopiedPath(path);
      setTimeout(() => {
        setCopiedPath(null);
      }, 2000);
    }).catch(err => {
      console.error('Failed to copy path:', err);
    });
  };

  return (
    <div className="card mb-4">
      <div className="card-header">
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Binaries</h5>
          <div>
            <button 
              className="btn btn-sm btn-outline-primary me-2" 
              onClick={onViewAll}
              disabled={loadingFiles}
            >
              <FontAwesomeIcon icon={faList} className="me-1" /> View All
            </button>
            <button 
              className="btn btn-sm btn-outline-secondary" 
              onClick={onRefresh}
              disabled={loadingFiles}
            >
              {loadingFiles ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                  Refreshing...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faSyncAlt} className="me-1" /> Refresh
                </>
              )}
            </button>
          </div>
        </div>
      </div>
      <div className="card-body">
        {/* Upload Section - Floating left */}
        <div className="d-flex mb-3">
          <div className="me-auto border rounded p-3 bg-light" style={{ maxWidth: '600px' }}>
            <div className="d-flex align-items-center mb-2">
              <strong className="me-2">
                <FontAwesomeIcon icon={faUpload} className="me-1" /> Upload New Binary
              </strong>
            </div>
            <div className="d-flex flex-wrap gap-2 align-items-center">
              <div className="flex-grow-1" style={{ minWidth: '250px' }}>
                <input
                  type="file"
                  className="form-control form-control-sm"
                  id="firmwareFile"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept=".bin"
                />
              </div>
              <div>
                <button 
                  className="btn btn-sm btn-primary" 
                  onClick={handleFileUpload}
                  disabled={uploading || !uploadFileName}
                >
                  {uploading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faUpload} className="me-1" /> Upload
                    </>
                  )}
                </button>
              </div>
            </div>
            
            {uploadError && (
              <div className="alert alert-danger mt-2 mb-0 p-2 small" role="alert">
                {uploadError}
              </div>
            )}
            
            {uploadSuccess && (
              <div className="alert alert-success mt-2 mb-0 p-2 small" role="alert">
                Firmware file uploaded successfully!
              </div>
            )}
          </div>
        </div>

        {/* Feedback Messages */}
        {loadingFiles ? (
          <div className="text-center py-3">
            <div className="spinner-border spinner-border-sm" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <span className="ms-2">Loading firmware files...</span>
          </div>
        ) : firmwareFiles.length > 0 ? (
          <>
            <div className="table-responsive">
              <table className="table table-striped table-hover">
                <thead>
                  <tr>
                    <th>Filename</th>
                    <th>Path</th>
                    <th>Uploaded</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {limitedFirmwareFiles.map((file, index) => (
                    <tr key={index}>
                      <td>{file.name}</td>
                      <td><code>{file.path}</code></td>
                      <td>{file.uploadedAt ? new Date(file.uploadedAt).toLocaleString() : 'Unknown'}</td>
                      <td>
                        <button
                          className="btn btn-sm btn-outline-secondary me-2"
                          onClick={() => handleCopyPath(file.path)}
                          title="Copy Full Path"
                        >
                          <FontAwesomeIcon icon={copiedPath === file.path ? faCheck : faCopy} />
                        </button>
                        <a 
                          href={`${apiBaseUrl}${file.path}`} 
                          className="btn btn-sm btn-outline-primary me-2"
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                          title="Download"
                        >
                          <FontAwesomeIcon icon={faDownload} />
                        </a>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => onDelete(file.name)}
                          title="Delete"
                        >
                          <FontAwesomeIcon icon={faTrashAlt} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {firmwareFiles.length > limitDisplay && (
              <div className="text-center mt-3">
                <small className="text-muted">
                  Showing {limitDisplay} of {firmwareFiles.length} binary files. 
                  <button 
                    className="btn btn-link btn-sm p-0 ms-1"
                    onClick={onViewAll}
                  >
                    View all bins
                  </button>
                </small>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-3">
            <p className="text-muted mb-0">No firmware files available.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BinariesManager;